import { useState } from 'react';
import { Descriptions, Flex } from 'antd';
import { CounterpartySelect, useListCounterpartiesQuery } from '@/entities/counterparty';
import { useLazyExportProfitXlsxQuery, useProfitReportQuery } from '@/entities/reports';
import type { ProfitReportLine } from '@/entities/reports';
import { ReportPage } from '@/shared/ui';
import type { ResponsiveColumns } from '@/shared/ui';
import { useBreakpoint } from '@/shared/lib/hooks';
import { useStoreContext } from '@/shared/lib/store-context';
import { todayInTz, formatDate } from '@/shared/lib/date';
import { displaySigned } from '@/shared/lib/money';
import { PeriodFilter } from './PeriodFilter';
import { presetRange } from './periodPresets';

const columns: ResponsiveColumns<ProfitReportLine> = [
  {
    title: '',
    dataIndex: 'label',
    render: (label: string, line: ProfitReportLine) => (line.kind === 'line' ? label : <b>{label}</b>),
  },
  {
    title: 'Сумма',
    dataIndex: 'amount',
    width: 160,
    align: 'right' as const,
    // Телефон: сумма — справа в той же строке, что и название статьи.
    mobile: 'badge',
    render: (amount: string, line: ProfitReportLine) => (line.kind === 'line' ? displaySigned(amount) : <b>{displaySigned(amount)}</b>),
  },
];

/** §13, §11.3–11.5: «Финансовые отчёты» — точная прибыль за период, по всему магазину или по одному
 *  контрагенту. Строки и знаки приходят с сервера — те же, что в файле Excel: вычитаемые статьи
 *  с минусом, итог равен сумме строк. */
export function ProfitReportPage() {
  const ctx = useStoreContext();
  const { isMobile } = useBreakpoint();
  const today = todayInTz(ctx.timezone);
  const defaultPeriod = presetRange('month', today);
  const [from, setFrom] = useState(defaultPeriod.from);
  const [to, setTo] = useState(defaultPeriod.to);
  const [counterpartyId, setCounterpartyId] = useState<string>();
  const periodChanged = from !== defaultPeriod.from || to !== defaultPeriod.to;
  const params = { from, to, counterpartyId };
  const { data, isFetching } = useProfitReportQuery(params, { pollingInterval: 30_000 });
  const [exportXlsx] = useLazyExportProfitXlsxQuery();
  // Тот же запрос, что делает поле выбора, — из кэша; нужен только ради названия в сводке фильтров.
  const { data: counterparties = [] } = useListCounterpartiesQuery({});
  const counterpartyName = counterpartyId ? counterparties.find((c) => c.id === counterpartyId)?.name : undefined;

  // Итог показан отдельно, под таблицей.
  const rows = (data?.lines ?? []).filter((line) => line.kind !== 'total');

  return (
    <ReportPage
      title="Финансовые отчёты"
      filters={
        <Flex vertical={isMobile} wrap={!isMobile} gap={isMobile ? 12 : 8} align={isMobile ? 'stretch' : 'center'}>
          <PeriodFilter
            from={from}
            to={to}
            today={today}
            onChange={(range) => {
              setFrom(range.from);
              setTo(range.to);
            }}
          />
          <div style={isMobile ? undefined : { width: 260 }}>
            <CounterpartySelect value={counterpartyId} onChange={setCounterpartyId} placeholder="Все контрагенты" />
          </div>
        </Flex>
      }
      summary={
        data ? (
          <Flex justify="flex-end" className="no-print">
            <Descriptions size="small" column={1} bordered style={{ maxWidth: 360 }}>
              <Descriptions.Item label="Итого прибыль">
                <b>{displaySigned(data.totalProfit)}</b>
              </Descriptions.Item>
            </Descriptions>
          </Flex>
        ) : null
      }
      columns={columns}
      activeFilters={(periodChanged ? 1 : 0) + (counterpartyId ? 1 : 0)}
      onResetFilters={() => {
        setFrom(defaultPeriod.from);
        setTo(defaultPeriod.to);
        setCounterpartyId(undefined);
      }}
      filtersSummary={`${formatDate(from)} — ${formatDate(to)}${counterpartyName ? ` · ${counterpartyName}` : ''}`}
      data={rows}
      loading={isFetching}
      rowKey="key"
      onExport={() => exportXlsx(params).unwrap()}
      exportFilename="Финансовые отчёты.xlsx"
    />
  );
}
